import type { CustomForm } from "~/types/form"

export function useForm<T>(
    obj: Record<string, any>,
    id?: string | number,
    resource?: string
): CustomForm<T> {
    const initialObj = { ...obj }
    const objKeys = Object.keys(obj)
    const data = ref(obj)
    const errors = ref<Record<string, any>>({})
    const {
        loading: processing,
        get: fGet,
        put: fPut,
        post: fPost,
        destroy: fDestroy,
        setUrl,
    } = useOFetchCustom("")

    function setData(obj: Record<string, any>) {
        data.value = { ...initialObj, ...obj }
    }
    function setErrors(newErrors: Record<string, any>) {
        errors.value = newErrors
    }

    function clearErrors() {
        errors.value = {}
    }

    function clearError(field: string) {
        delete errors.value[field]
    }

    function reset() {
        Object.keys(obj).forEach((key) => (data.value[key] = initialObj[key]))
    }

    async function get(url: string): Promise<any> {
        setUrl(url)

        return new Promise((resolve, reject) => {
            fGet(null, {
                onSuccess: (res: any) => {
                    resolve(res)
                },
                onError: (error: any) => {
                    reject(error)
                },
            })
        })
    }

    async function post(
        url: string,
        options?: { withFiles?: boolean; additionalData: any }
    ): Promise<any> {
        setUrl(url)

        let formData =
            options && options.withFiles == true
                ? objectToFormData(data.value)
                : data.value
        if (options?.additionalData) {
            formData = { ...formData, ...options.additionalData }
        }

        return new Promise((resolve, reject) => {
            fPost(formData, {
                onSuccess: (res: any) => {
                    resolve(res)
                },
                onError: (error: any) => {
                    const data = error._data ?? error.data
                    if (error.status == 422) {
                        errors.value = data.errors
                    }
                    reject(error)
                },
            })
        })
    }

    async function put(
        url: string,
        options?: { withFiles?: boolean }
    ): Promise<any> {
        setUrl(url)

        const formData =
            options && options.withFiles == true
                ? objectToFormData(data.value)
                : data.value

        return new Promise((resolve, reject) => {
            fPut(formData, {
                onSuccess: (res: any) => {
                    resolve(res)
                },
                onError: (error: any) => {
                    console.log(error)
                    const data = error._data ?? error.data
                    if (error.status == 422) {
                        errors.value = data.errors
                    }
                    reject(error)
                },
            })
        })
    }

    async function destroy(
        url: string,
        body?: Record<string, any>
    ): Promise<any> {
        setUrl(url)

        return new Promise((resolve, reject) => {
            fDestroy(body, {
                onSuccess: (res: any) => {
                    resolve(res)
                },
                onError: (error: any) => {
                    reject(error)
                },
            })
        })
    }

    const handler = {
        get: (target: any, prop: string) => {
            if (objKeys.includes(prop)) {
                return data.value[prop]
            }

            if (prop == "errors") {
                return errors.value
            }

            if (prop == "processing") {
                return processing.value
            }

            return Reflect.get(target, prop)
        },
        set: (target: any, prop: string, value: any) => {
            if (objKeys.includes(prop)) {
                data.value[prop] = value
                if (errors.value && errors.value[prop]) {
                    clearError(prop)
                }
            }

            return Reflect.set(target, prop, value)
        },
    }

    function objectToFormData(
        obj: any,
        formData = new FormData(),
        parentKey = ""
    ): FormData {
        if (!isObject(obj) || obj instanceof File || obj instanceof Blob) {
            return appendFormData(formData, obj, parentKey)
        }

        Object.keys(obj).forEach((key) => {
            const value = obj[key]
            const formKey = parentKey ? `${parentKey}[${key}]` : key

            if (value === null || value === undefined) {
                appendFormData(formData, "", formKey)
            } else {
                handleValue(value, formKey, formData)
            }
        })

        return formData
    }

    function handleValue(
        value: any,
        formKey: string,
        formData: FormData
    ): void {
        if (Array.isArray(value)) {
            processArray(value, formKey, formData)
        } else if (isObject(value)) {
            objectToFormData(value, formData, formKey) // Recursive call
        } else if (value instanceof File || value instanceof Blob) {
            appendFormData(formData, value, formKey)
        } else if (typeof value === "boolean") {
            appendFormData(formData, value ? 1 : 0, formKey)
        } else {
            appendFormData(formData, value, formKey)
        }
    }

    function isObject(value: any): boolean {
        return (
            typeof value === "object" && value !== null && !Array.isArray(value)
        )
    }

    function appendFormData(
        formData: FormData,
        value: any,
        key: string
    ): FormData {
        formData.append(key, value)
        return formData
    }

    function processArray(array: any[], formKey: string, formData: FormData) {
        array.forEach((item, index) => {
            const itemKey = `${formKey}[${index}]`
            objectToFormData(item, formData, itemKey)
        })
    }

    onMounted(() => {
        if (id && resource) {
            get(resource).then((res) => {
                data.value = { ...initialObj, ...res.data }
            })
        }
    })

    const form = {
        data,
        errors,
        processing,
        clearErrors,
        reset,
        get,
        post,
        put,
        destroy,
        setErrors,
        clearError,
        setData,
    }

    return new Proxy(form, handler)
}
